import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import * as yaml from 'js-yaml';
import { Drawer, Dropdown, Segmented, Switch } from 'antd';
import Icon from './icons/Icon';
import SearchDropdown from './SearchDropdown';
import OpenInMenu from './OpenInMenu';
import PublishDialog from './PublishDialog';
import ShareDialog from './ShareDialog';
import { NOTES_DESC } from './collab/NotesModal';
import MessagesHost, { openMessages } from './collab/Messages';
import { canMessageAgents } from '../utils/messagesModel';
import { codeSearch } from './traces/tracesModel';
import unifiedGitDataService from '../services/UnifiedGitDataService';
import { DEFAULT_MICROSERVICE_BRANCH } from '../constants/gitConstants';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import * as vfsClient from '../utils/vfsClient';
import CollaborationPanel from './CollaborationPanel';
import { useAuth } from '../context/AuthContext';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import './UnifiedTopBar.css';
import './ProjectTierBadge.css';
import { devLog } from '../utils/devLog';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import ProjectWorkspaceChips from './workspaces/ProjectWorkspaceChips';
import useChatFeature from '../hooks/useChatFeature';
import ProjectRunStatus from './sandbox/ProjectRunStatus';
import ProjectRunTools from './sandbox/ProjectRunTools';
import useProjectPresence, { usePresenceView } from '../hooks/useProjectPresence';
import { PresenceStack } from './presence/Presence';
import { ChatCircleDots, ChatTeardropText, Chats, ClockCounterClockwise, DotsThreeVertical, Eye, FolderSimple, Notebook, SquaresFour } from '@phosphor-icons/react';
import useMediaQuery, { useCompact, useNarrowElement, usePhone } from '../hooks/useMediaQuery';
import WorkspaceTabs, { TeamMenu, WorkspacePanelToggle } from './workspace/WorkspaceTabs';
import { VIEW_TABS, currentWorkspaceTab, previewWatchers, roleLabel, viewFromHistory } from '../utils/workspaceModel';
import { listedAgents } from './presence/Presence';
import { othersOf, splitVfsPath } from '../utils/presenceModel';
import {
  AppWindow, Archive, CalendarCheck, ChartBar, CurrencyEur, Database, EnvelopeSimple, Files, FlowArrow, GitBranch, List, Plug, PlugsConnected, PushPinSimple,
  RocketLaunch, ShareNetwork, ShieldCheck, SlidersHorizontal, Terminal, TerminalWindow,
} from '@phosphor-icons/react';
import useProjectTabs from '../hooks/useProjectTabs';
import useHideOnScroll from '../hooks/useHideOnScroll';
import useProjectServices from '../hooks/useProjectServices';
import { preferredService, rememberService, scopeDotState } from '../utils/scopeModel';
import { withRunnableItems } from '../utils/runnablePreview';
import { ADVANCED_TABS, TAB_GROUPS, TEAM_GROUP, isTabHidden } from '../utils/projectTabsModel';
import { SearchTrigger } from './search/SearchContext';
import {
  buildProjectCommands, notesCommands, projectServiceItems, runCommands, useProjectCommandSource,
} from './search/projectCommands';
import { openApp } from './sandbox/projectRun';
import './workspace/Workspace.css';
import { PROVIDE_MISSING_EVENT } from './sandbox/StartButton';

/**
 * The project header's "Projects / name" and your role in the project (Owner, Editor, Viewer), with the workspace tabs
 * (collaboration phase 4): Projects goes to the list, the name to the project's Dashboard. Sandbox-first: the
 * workspaces the project is in, as chips (a project may be in several; each opens its workspace).
 */
const WorkspaceCrumb = ({ projectId, projectName, role, onProjects, onHome }) => (
  <div className="workspace-crumb" data-testid="workspace-crumb">
    <nav aria-label="Breadcrumb" className="workspace-crumb-path">
      <button className="workspace-crumb-link" onClick={onProjects} type="button">Projects</button>
      <span aria-hidden="true" className="workspace-crumb-sep">/</span>
      <button aria-label={projectName ? `${projectName}: project Dashboard` : 'Project Dashboard'} className="workspace-crumb-name" onClick={onHome} title={projectName} type="button">
        {projectName || 'Project'}
      </button>
    </nav>
    {roleLabel(role) && <span className={`workspace-role-chip is-${role}`} data-testid="role-chip" title={`You are ${role === 'owner' ? 'the owner' : `an ${role === 'editor' ? 'editor' : 'viewer'}`} of this project`}>{roleLabel(role)}</span>}
    {config.sandboxFirst && projectId && <ProjectWorkspaceChips projectId={projectId} projectName={projectName} />}
  </div>
);

WorkspaceCrumb.propTypes = {
  onHome: PropTypes.func,
  onProjects: PropTypes.func,
  projectId: PropTypes.string,
  projectName: PropTypes.string,
  role: PropTypes.string,
};

/**
 * Project identity shown at the start of the Code and Preview tabs' center
 * content, left of the page/service selector: the project's title (folder
 * badge + name, single line) as one button that goes to the project's
 * overview (Home). When a service is open, hovering swaps the folder badge
 * for a back arrow — that's what the click does. No separate "switch
 * project" control here: the Projects list is already one click away in
 * the left sidebar.
 */
const ProjectBreadcrumb = ({ projectName, onHomeClick, atHome }) => (
  <div className="project-breadcrumb-unified">
    <button
      aria-label={projectName ? `${projectName} — project overview` : 'Project overview'}
      className={`breadcrumb-home-btn${atHome ? ' at-home' : ''}`}
      onClick={onHomeClick}
      title={atHome ? projectName || 'Project overview' : 'Back to the project overview'}
      type="button"
    >
      <span className="breadcrumb-project-badge" aria-hidden="true">
        <span className="breadcrumb-project-badge-icon"><Icon name="folder" size={14} /></span>
        <span className="breadcrumb-project-badge-back"><Icon name="arrow-left" size={14} /></span>
      </span>
      {projectName && <span className="breadcrumb-home-btn-project-name" title={projectName}>{projectName}</span>}
    </button>
    <span className="breadcrumb-separator" aria-hidden="true">/</span>
  </div>
);

ProjectBreadcrumb.propTypes = {
  projectName: PropTypes.string,
  onHomeClick: PropTypes.func,
  atHome: PropTypes.bool,
};

/**
 * Friendly label for a service dropdown entry. `service.name` is a real
 * vfsServiceName (a vfs-service path, not a display label) in two shapes:
 * '.' (old convention — the project root itself IS the single app, kept for
 * backward compat with any project still in that shape) and
 * 'services/<name>' (current convention — every real service lives under
 * its own subfolder of `services/`, see Canvas.jsx's loadConfiguration). A
 * project-supplied `displayName` always wins when present.
 */
const serviceDisplayLabel = (service) => {
  if (service.displayName) {
    return service.displayName;
  }
  if (service.name === '.') {
    return 'App (project root)';
  }
  if (service.name?.startsWith('services/')) {
    return service.name.slice('services/'.length);
  }
  return service.name;
};

/**
 * Short one-line description for a service row in the search-dropdown
 * picker (task: "that dropdown must be reused in Service selection" —
 * matching the playbook picker's icon+title+desc rows). Services don't
 * carry a free-text description the way playbooks do, so this falls back
 * to whatever's actually informative: the repo it's backed by, or that
 * it's a vfs-only project with no external repo.
 */
const serviceDesc = (service) => {
  const repoUrl = service.repositoryUrl || service.repoUrl || service.sshUrl;
  if (repoUrl) {
    try {
      const url = new URL(repoUrl.replace(/\.git$/, ''));
      return url.pathname.replace(/^\//, '');
    } catch {
      return repoUrl;
    }
  }
  if (service.vfsBacked) {
    return 'Built in Flow — no external repo';
  }
  return undefined;
};

/**
 * The service segment of a preview URL (`/api/preview/<project>/services/<service>/proxy/`): the
 * vfs folder name without its `services/` prefix; none for the project root ('.').
 */
const previewServiceName = (name) => {
  if (!name || name === '.') {
    return undefined;
  }
  return name.startsWith('services/') ? name.slice('services/'.length) : name;
};

/**
 * UnifiedTopBar Component
 * A dynamic, space-efficient top bar that changes content based on active tab
 * Implements the design from the mockups: minimal when inactive, expands when active
 */
// Every place of the phone's Menu has its own icon (design "Flow Project Redesign", Mobile board).
const SHEET_ICONS = {
  apps: AppWindow, files: Files, preview: Eye, code: FolderSimple, git: GitBranch, terminal: TerminalWindow, data: Database, mail: EnvelopeSimple,
  api: PlugsConnected, mcp: Plug, quality: ShieldCheck, config: SlidersHorizontal, history: ClockCounterClockwise, traces: FlowArrow, logs: Terminal,
  publish: RocketLaunch, jobs: CalendarCheck, finops: CurrencyEur, backups: Archive, usage: ChartBar,
  share: ShareNetwork, message: ChatCircleDots, messages: Chats, notes: Notebook, chat: ChatTeardropText,
};

/**
 * The phone's Menu sheet (design "Flow Project Redesign", Mobile board): the bar keeps Home · Apps · Files · Menu, so
 * the rest is here by the step of the work, as the desktop's group menus: Build (Preview, then its tabs, then History
 * with the Changes count), Verify, Ship (Publish and deploys), Operate (its tabs, then Backups, Usage), Team
 * (Share, Messages, Notes), then "Turn on chat" where this person's plan has chat and it is off (`onChat`).
 * Each tab with its pin, and Show advanced tabs. `actions`: the header's group actions (UnifiedTopBar `groupActions`).
 */
function ProjectMoreSheet({ open, onClose, onOpen, tabs, current = null, actions = {}, changesCount = 0, previewDisabled = false, onChat = null }) {
  const onBar = (tab) => tab.id === 'apps' || tab.id === 'files';
  const groups = [...TAB_GROUPS, TEAM_GROUP].map((group) => ({
    id: group.id,
    title: group.label,
    lead: group.id === 'build' && !isTabHidden(tabs.hidden, 'preview')
      ? [{ id: 'preview', label: 'Preview', short: 'Run the app and open its link', disabled: previewDisabled }] : [],
    items: tabs.more.filter((tab) => tab.group === group.id && !onBar(tab)),
    actions: actions[group.id] || [],
    history: group.id === 'build' && !isTabHidden(tabs.hidden, 'history'),
  })).filter((group) => group.lead.length + group.items.length + group.actions.length > 0 || group.history);
  const plainRow = ({ id, label, short, count = 0, onClick, disabled = false, testid }) => {
    const RowIcon = SHEET_ICONS[id] || PlugsConnected;
    return (
      <div className="compact-sheet-item" key={id}>
        <button
          aria-current={current === id ? 'page' : undefined}
          className={`compact-sheet-row${current === id ? ' is-on' : ''}`}
          data-testid={testid}
          disabled={disabled}
          onClick={onClick}
          type="button"
        >
          <RowIcon aria-hidden size={20} />
          <span className="compact-sheet-text">
            <strong>{label}{count > 0 && <span aria-label={`${count} new`} className="workspace-tab-count">{count > 99 ? '99+' : count}</span>}</strong>
            {short && <span>{short}</span>}
          </span>
        </button>
        {/* The pin's slot, empty: every row is as wide as a pinnable one. */}
        <span aria-hidden="true" className="compact-sheet-slot" />
      </div>
    );
  };
  return (
    <Drawer height="auto" onClose={onClose} open={open} placement="bottom" rootClassName="compact-more-sheet" title="Menu">
      <div data-testid="compact-more-sheet">
        {/* Every tab and action of the project, and the rest of Flow: the same palette as ⌘K. */}
        <SearchTrigger className="compact-sheet-search" label="Search this project and Flow" onBeforeOpen={onClose} />
        {groups.map((group) => (
          <div aria-label={group.title} className="compact-sheet-group" data-testid={`compact-sheet-${group.id}`} key={group.id} role="group">
            <div className="compact-sheet-cap">{group.title}</div>
            {group.lead.map((tab) => plainRow({ ...tab, onClick: () => onOpen(tab.id), testid: `compact-sheet-open-${tab.id}` }))}
            {group.items.map((tab) => {
              const TabIcon = SHEET_ICONS[tab.id] || PlugsConnected;
              return (
                <div className="compact-sheet-item" key={tab.id}>
                  <button
                    aria-current={current === tab.id ? 'page' : undefined}
                    className={`compact-sheet-row${current === tab.id ? ' is-on' : ''}`}
                    data-testid={`compact-sheet-open-${tab.id}`}
                    onClick={() => onOpen(tab.id)}
                    type="button"
                  >
                    <TabIcon aria-hidden size={20} />
                    <span className="compact-sheet-text">
                      <strong>
                        {tab.label}
                        {tab.count > 0 && <span aria-label={`${tab.count} blocking`} className="workspace-tab-count is-bad">{tab.count > 99 ? '99+' : tab.count}</span>}
                        {tab.shown === 'auto' && !tab.pinned && <span className="compact-sheet-auto" title="Shown automatically for this project">Auto</span>}
                      </strong>
                      <span>{tab.short}</span>
                    </span>
                  </button>
                  <button
                    aria-label={`${tab.pinned ? 'Unpin' : 'Pin'} ${tab.label}`}
                    aria-pressed={tab.pinned}
                    className={`compact-sheet-pin${tab.pinned ? ' is-on' : ''}`}
                    data-testid={`compact-sheet-pin-${tab.id}`}
                    onClick={() => tabs.togglePin(tab.id)}
                    type="button"
                  >
                    <PushPinSimple aria-hidden size={18} weight={tab.pinned ? 'fill' : 'regular'} />
                  </button>
                </div>
              );
            })}
            {group.actions.map((action) => plainRow({
              id: action.id, label: action.label, short: action.desc, count: action.count || 0, onClick: () => { onClose(); action.onSelect(); }, testid: `compact-sheet-action-${action.id}`,
            }))}
            {group.history && plainRow({
              id: 'history', label: 'History', short: 'Changes, Activity and Sessions', count: changesCount, onClick: () => onOpen('history'), testid: 'compact-sheet-open-history',
            })}
          </div>
        ))}
        {onChat && (
          <div className="compact-sheet-group" data-testid="compact-sheet-chat-group">
            {plainRow({ id: 'chat', label: 'Turn on chat', short: 'Talk to Flow\'s assistant in your projects', onClick: () => { onClose(); onChat(); }, testid: 'compact-sheet-chat' })}
          </div>
        )}
        <div className="compact-sheet-foot">
          <Switch checked={tabs.allPinned} id="compact-sheet-all" onChange={(on) => tabs.setAllPinned(on)} />
          <label htmlFor="compact-sheet-all">Show advanced tabs in every project</label>
        </div>
        <p className="compact-sheet-note">A pinned tab is also on the tab row on a wider screen.</p>
      </div>
    </Drawer>
  );
}

ProjectMoreSheet.propTypes = {
  actions: PropTypes.object,
  changesCount: PropTypes.number,
  current: PropTypes.string,
  onChat: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  open: PropTypes.bool,
  previewDisabled: PropTypes.bool,
  tabs: PropTypes.object.isRequired,
};

const UnifiedTopBar = ({
  activeTab,
  tenantId,
  selectedProject,
  selectedResource,
  configuration,
  onTabChange,
  onToggleFullscreen,
  isFullscreen,
  onServiceSelect,
  filesScope = null,
  onFilesRoot = null,
  onRefresh,
  onNewConversation,
  onShowHistory,
  onOpenHistory,
  onOpenNotes,
  suggestionsPending = 0,
  showHistoryButton = false,
  splitViewMode = 'editor',
  onSplitViewModeChange,
  previewTabs = [],
  pinnedPreviews = [],
  showDashboardTab = false,
  dashboardOverviewMode = 'dashboard',
  onDashboardOverviewModeChange,
  onProjectAccessChange,
  workspace = null,
}) => {
  const { user } = useAuth();
  const navigateTo = useNavigate();
  // Turn on chat: only when the person's plan includes chat (useChatFeature's `available`).
  const [chatFeatureOn, , chatAvailable] = useChatFeature();
  const chatOffer = Boolean(config.sandboxFirst && !chatFeatureOn && chatAvailable);
  // Phones and tablets in portrait: one row with the page and an overflow menu, then Dashboard / Files / Preview / History.
  // ...or wherever the bar itself is narrow (the Canvas beside the chat on a 1024px screen).
  const barRef = useRef(null);
  const narrowBar = useNarrowElement(barRef, 700);
  const compactWindow = useCompact();
  const compact = compactWindow || narrowBar;
  const phone = usePhone();
  // A phone: the header scrolls away with the page and comes back on scrolling up (ProjectCompact.css); a new page
  // starts at its top.
  const headerAway = useHideOnScroll(barRef, phone && compact, [
    selectedProject?.id, workspace?.view?.tab, activeTab, dashboardOverviewMode, selectedResource?.data?.name,
  ].join('|'));
  // Desktop below 1280px: the collaboration buttons, Share and Publish move into a "More" menu, so the header never
  // overlaps itself.
  const mediumWindow = useMediaQuery('(max-width: 1279px)');
  const mediumBar = useNarrowElement(barRef, 900);
  const medium = mediumWindow || mediumBar;
  // A bar under 980px: the Team menu (the bar without the workspace tabs) shows its icon only.
  const tightBar = useNarrowElement(barRef, 980);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [tags, setTags] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(false);
  const [showDeployDropdown, setShowDeployDropdown] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  // The Quality tab's "Publish" (all clear) opens this dialog.
  useEffect(() => {
    const openPublish = () => setShowPublish(true);
    window.addEventListener('flow:open-publish', openPublish);
    return () => window.removeEventListener('flow:open-publish', openPublish);
  }, []);
  const [showShare, setShowShare] = useState(false);
  const openShare = () => setShowShare(true);
  // A start refused for files the project lacks (StartButton's "Provide the missing files"): Apps on that service, with
  // the form open.
  const workspaceRef = useRef(workspace);
  workspaceRef.current = workspace;
  useEffect(() => {
    const provide = (event) => {
      const serviceName = event?.detail?.serviceName;
      if (serviceName) { workspaceRef.current?.onViewChange?.({ tab: 'apps', focus: { service: serviceName, provideMissing: true } }); }
    };
    window.addEventListener(PROVIDE_MISSING_EVENT, provide);
    return () => window.removeEventListener(PROVIDE_MISSING_EVENT, provide);
  }, []);
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  const [pipelineLogs, setPipelineLogs] = useState([]);
  const [currentPipeline, setCurrentPipeline] = useState(null);
  const deployDropdownRef = useRef(null);
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const viewDropdownRef = useRef(null);

  // Project storage tier (vfs -> catalog -> console) — see
  // mia-flow-bff's lib/project-store.js for the full pipeline. Display-only
  // here now (task: removed "Persist to Catalog"/"Launch in Console" actions).
  const projectStorageTier = selectedProject?.storageTier || 'vfs';
  // Live presence (utils/presenceClient.js): who else is in this project, and where this tab is (a service or Home;
  // the editor publishes the open file itself).
  const presenceProjectId = selectedProject?.id && projectStorageTier !== 'console' ? selectedProject.id : null;
  const presence = useProjectPresence(presenceProjectId, { name: user?.name || user?.email || '' });
  usePresenceView(presenceProjectId, 'page', selectedResource?.type === 'service'
    ? { kind: 'service', service: selectedResource.data?.name || undefined }
    : { kind: 'home' });

  // Pipeline history modal state
  const pipelinePollingIntervalRef = useRef(null);
  const previousPipelineStatusRef = useRef(null);

  // Extract GitLab project ID from repository URL
  const getGitlabProjectId = (service) => {
    if (!service) return null;
    const repositoryUrl = service.repositoryUrl || service.repoUrl || service.sshUrl;
    if (!repositoryUrl) return null;
    
    try {
      const url = new URL(repositoryUrl.replace('.git', ''));
      const pathParts = url.pathname.split('/').filter(Boolean);
      return pathParts.join('/');
    } catch (err) {
      return null;
    }
  };

  const getRepositoryUrl = (service) => {
    if (!service) return null;
    return service.repositoryUrl || service.repoUrl || service.sshUrl;
  };

  /**
   * Pre-flight: check if the git provider tool is enabled and the user is authenticated.
   * Returns { toolEnabled, authenticated, providerName }.
   * Fails open (toolEnabled=true) if the tools API is unreachable, so we never
   * silently block a working integration due to a transient network issue.
   */
  const checkGitProviderStatus = async (repoUrl) => {
    if (!repoUrl) return { toolEnabled: false, authenticated: false, providerName: 'Git' };
    try {
      const provider = unifiedGitDataService.detectProvider(repoUrl);
      const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';

      // 1. Tool enablement (fail-open on network error)
      let toolEnabled = true;
      try {
        const { getBffHeaders } = await import('../utils/apiHeaders');
        const { default: cfg } = await import('../config');
        const bffUrl = cfg.bffUrl || window.location.origin;
        const storedTenant = localStorage.getItem('mia_selectedTenant');
        const tenantId = storedTenant ? JSON.parse(storedTenant).tenantId : null;
        const toolsParams = tenantId ? `?tenantId=${tenantId}` : '';
        const res = await getAuthorizedFetch()(`${bffUrl}/api/catalog/ai-tools${toolsParams}`, { headers: getBffHeaders() });
        if (res.ok) {
          const data = await res.json();
          const allTools = data?.data?.items || [];
          const providerTools = allTools.filter(t => {
            const cat = (t.category || '').toLowerCase();
            const name = (t.name || '').toLowerCase();
            const runtimeName = (t.runtimeName || '').toLowerCase();
            if (provider === 'github') return cat.includes('github') || name.includes('github') || runtimeName.includes('github');
            if (provider === 'azuredevops') return cat.includes('azure') || name.includes('azure') || runtimeName.includes('azure_devops');
            return cat.includes('gitlab') || name.includes('gitlab') || runtimeName.includes('gitlab');
          });
          if (providerTools.length > 0) toolEnabled = providerTools.some(t => t.enabled !== false);
        }
      } catch { toolEnabled = true; } // fail-open

      // 2. Authentication
      const authStatus = await unifiedGitDataService.checkAuthentication(provider);
      return { toolEnabled, authenticated: authStatus.authenticated, providerName };
    } catch {
      return { toolEnabled: true, authenticated: false, providerName: 'Git' };
    }
  };

  // Fetch branches and tags when service changes
  useEffect(() => {
    const loadGitRefs = async () => {
      if (!selectedResource || selectedResource.type !== 'service') {
        setBranches([]);
        setTags([]);
        setCurrentBranch(null);
        return;
      }

      const gitlabProjectId = getGitlabProjectId(selectedResource.data);
      if (!gitlabProjectId) return;

      const repositoryUrl = getRepositoryUrl(selectedResource.data);
      if (!repositoryUrl) return;

      // ── PRE-FLIGHT: check tool enabled + authenticated BEFORE any git API call ──
      const gitStatus = await checkGitProviderStatus(repositoryUrl);
      if (!gitStatus.toolEnabled || !gitStatus.authenticated) {
        // eslint-disable-next-line no-console
        console.warn(`🚫 [TopBar] Skipping git refs load — ${gitStatus.toolEnabled ? 'not authenticated' : 'tool disabled'} for ${gitStatus.providerName}`);
        setBranches([]);
        setTags([]);
        setCurrentBranch(null);
        return;
      }
      // ─────────────────────────────────────────────────────────────────────────

      // Reset branch while loading so the pipeline effect waits for the real value
      setCurrentBranch(null);

      try {
        setLoadingRefs(true);
        
        // Fetch branches and tags separately to handle errors gracefully
        const branchList = await unifiedGitDataService.listBranches(gitlabProjectId, repositoryUrl);
        setBranches(branchList);
        
        // Try to fetch tags, but don't fail if endpoint doesn't exist
        try {
          const tagList = await unifiedGitDataService.listTags(gitlabProjectId, repositoryUrl);
          setTags(tagList);
        } catch (tagError) {
          devLog('Tags not available (this is normal for some GitLab instances):', tagError.message); /* eslint-disable-line no-console */
          setTags([]);
        }

        // Check for saved ref in localStorage
        const savedRef = localStorage.getItem(`git_ref_${selectedProject?.id}_${selectedResource.data?.name}`);
        if (savedRef) {
          setCurrentBranch(savedRef);
        } else {
          // Get default branch from project info
          const projectInfo = await unifiedGitDataService.getProject(gitlabProjectId, repositoryUrl);
          setCurrentBranch(projectInfo.default_branch || DEFAULT_MICROSERVICE_BRANCH);
        }
      } catch (error) {
        console.error('Failed to load git refs:', error); /* eslint-disable-line no-console */
      } finally {
        setLoadingRefs(false);
      }
    };

    loadGitRefs();
  }, [selectedResource, selectedProject]);


  // NOTE: there used to be an "auto-select project configuration by default"
  // effect here, duplicating Canvas.jsx's own auto-select effect
  // (Canvas.jsx:3355-3422). Two independent places resolving the same "what's
  // selected when nothing is yet" state raced with each other — this one, in
  // particular, only checked `configuration?.services` truthiness (an empty
  // `{}` for a service-less vfs project satisfies that) and had no way
  // to know the URL already named a real service, so it repeatedly hijacked
  // selection to the "Project Configuration" pseudo-resource and blanked the
  // preview panel. Removed outright rather than re-guarded again — Canvas's
  // own effect already owns this responsibility correctly.

  // Load initial pipeline status when component mounts or service changes
  // Also poll for updates when pipeline is running
  useEffect(() => {
    const loadPipelineStatus = async () => {
      if (!selectedResource || selectedResource.type !== 'service') {
        return;
      }

      // Wait until the real branch has been resolved from the server/localStorage
      if (!currentBranch) {
        return;
      }

      const gitlabProjectId = getGitlabProjectId(selectedResource.data);
      if (!gitlabProjectId) return;

      const repositoryUrl = getRepositoryUrl(selectedResource.data);
      if (!repositoryUrl) return;

      try {
        // Fetch latest pipeline for current branch
        const pipelines = await unifiedGitDataService.listWorkflowRuns(
          gitlabProjectId,
          repositoryUrl,
          currentBranch,
          1 // Only get the latest pipeline
        );

        if (pipelines && pipelines.length > 0) {
          const latestPipeline = pipelines[0];
          const previousStatus = previousPipelineStatusRef.current;
          const currentStatus = latestPipeline.status;
          
          // Broadcast the status so Canvas can update UnifiedTopBar prop
          window.dispatchEvent(new CustomEvent('servicePipelineStatusUpdate', {
            detail: { status: currentStatus }
          }));

          // Update previous status
          previousPipelineStatusRef.current = currentStatus;

          // If pipeline is still running or pending, continue polling
          if (latestPipeline.status === 'running' || latestPipeline.status === 'pending') {
            if (!pipelinePollingIntervalRef.current) {
              pipelinePollingIntervalRef.current = setInterval(loadPipelineStatus, 5000);
            }
          } else {
            // Pipeline finished, stop polling
            if (pipelinePollingIntervalRef.current) {
              clearInterval(pipelinePollingIntervalRef.current);
              pipelinePollingIntervalRef.current = null;
            }
          }
        } else {
          // No pipeline found, stop polling
          if (pipelinePollingIntervalRef.current) {
            clearInterval(pipelinePollingIntervalRef.current);
            pipelinePollingIntervalRef.current = null;
          }
        }
      } catch (error) {
        // Silently fail - pipeline status is optional
        console.error('Failed to load pipeline status:', error); /* eslint-disable-line no-console */
      }
    };

    loadPipelineStatus();

    // Cleanup polling on unmount or when dependencies change
    return () => {
      if (pipelinePollingIntervalRef.current) {
        clearInterval(pipelinePollingIntervalRef.current);
        pipelinePollingIntervalRef.current = null;
      }
    };
  }, [selectedResource, currentBranch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (viewDropdownRef.current && !viewDropdownRef.current.contains(event.target)) {
        setShowViewDropdown(false);
      }
      if (deployDropdownRef.current && !deployDropdownRef.current.contains(event.target)) {
        setShowDeployDropdown(false);
      }
    };

    if (showViewDropdown || showDeployDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showViewDropdown, showDeployDropdown]);

  const handleDeploy = async (environment) => {
    if (!selectedProject?.id) {
      alert('No project selected');
      return;
    }

    const startTime = Date.now();

    // Track deployment start
    if (window.analytics?.trackDeployment) {
      window.analytics.trackDeployment('started', {
        environment: environment.envId,
        projectId: selectedProject.id,
        serviceName: selectedResource?.data?.name
      });
    }

    try {
      setIsDeploying(true);
      setShowDeployDropdown(false);

      // Use "main" as the default project configuration revision
      // Note: This is the project configuration branch, not the microservice branch
      const configRevision = 'main';
      
      const result = await miaPlatformDataService.deployProject(
        selectedProject.id,
        environment.envId,
        'revision',
        configRevision
      );
      
      // Track deployment completion
      if (window.analytics?.trackDeployment) {
        window.analytics.trackDeployment('completed', {
          environment: environment.envId,
          duration: Date.now() - startTime,
          success: true,
          pipelineId: result.pipelineId || result.id
        });
      }
      
      // Track feature usage
      if (window.analytics?.trackFeature) {
        window.analytics.trackFeature('deployment', {
          environment: environment.envId
        });
      }
      
      // Track deployment
      if (window.analytics?.trackDeployment) {
        window.analytics.trackDeployment('deploy_all', {
          projectId: selectedProject.id,
          projectName: selectedProject.name,
          environment: environment.envId,
          environmentLabel: environment.label,
          pipelineId: result.pipelineId || result.id
        });
      }

      // Store pipeline info and show modal
      setCurrentPipeline({
        id: result.pipelineId || result.id,
        url: result.url,
        projectId: selectedProject.id,
        environment: environment.envId,
        environmentLabel: environment.label || environment.envId,
        gitlabProjectId: extractGitLabProjectId(result.url)
      });
      setPipelineLogs([
        `🚀 Deployment triggered for ${environment.label || environment.envId}`,
        `📋 Pipeline ID: ${result.pipelineId || result.id}`,
        `🔗 GitLab URL: ${result.url || 'N/A'}`,
        '⏳ Fetching pipeline jobs...'
      ]);
      setShowPipelineModal(true);

      // Start polling for pipeline status and logs
      if (result.pipelineId || result.id) {
        pollPipelineStatusAndLogs(
          selectedProject.id, 
          result.pipelineId || result.id, 
          environment.envId,
          extractGitLabProjectId(result.url)
        );
      }
    } catch (error) {
      console.error('Deployment failed:', error); /* eslint-disable-line no-console */
      alert(`❌ Deployment failed: ${error.message}`);
    } finally {
      setIsDeploying(false);
    }
  };

  // Extract GitLab project ID from pipeline URL
  const extractGitLabProjectId = (url) => {
    if (!url) return null;
    // URL format: https://git.tools.mia-platform.eu/clients/mia-platform/demo/.../Configurations/-/pipelines/985199
    const match = url.match(/https?:\/\/[^/]+\/(.+?)\/-\/pipelines/);
    return match ? match[1] : null;
  };

  const pollPipelineStatusAndLogs = async (projectId, pipelineId, environment, gitlabProjectId) => {
    let attempts = 0;
    const maxAttempts = 120; // Poll for up to 10 minutes (every 5 seconds)
    let lastJobsFetched = null;
    
    const poll = async () => {
      try {
        attempts++;
        const status = await miaPlatformDataService.getPipelineStatus(projectId, pipelineId, environment);
        
        const timestamp = new Date().toLocaleTimeString();
        let statusEmoji = '⏳';
        
        // Fetch GitLab pipeline jobs and logs if we have project ID
        if (gitlabProjectId && !lastJobsFetched) {
          try {
            const repositoryUrl = getRepositoryUrl(selectedResource?.data);
            const jobs = await unifiedGitDataService.getWorkflowJobs(gitlabProjectId, repositoryUrl, pipelineId);
            lastJobsFetched = Date.now();
            
            setPipelineLogs(prev => [
              ...prev,
              `\n📦 Found ${jobs.length} job(s):`,
              ...jobs.map(job => `  • ${job.name} [${job.status}]`)
            ]);

            // Fetch logs for each job
            for (const job of jobs) {
              if (job.status === 'running' || job.status === 'failed' || job.status === 'success') {
                try {
                  const repositoryUrl = getRepositoryUrl(selectedResource?.data);
                  const logs = await unifiedGitDataService.getJobTrace(gitlabProjectId, repositoryUrl, job.id);
                  setPipelineLogs(prev => [
                    ...prev,
                    `\n📝 Logs for ${job.name}:`,
                    `${'─'.repeat(50)}`,
                    logs || '(no logs available)',
                    `${'─'.repeat(50)}`
                  ]);
                } catch (logError) {
                  console.error(`Error fetching logs for job ${job.id}:`, logError); /* eslint-disable-line no-console */
                }
              }
            }
          } catch (jobsError) {
            console.error('Error fetching pipeline jobs:', jobsError); /* eslint-disable-line no-console */
            setPipelineLogs(prev => [...prev, `${timestamp} ⚠️ Could not fetch GitLab logs: ${jobsError.message}`]);
          }
        }
        
        // Update status
        if (status.status === 'success') {
          statusEmoji = '✅';
          setPipelineLogs(prev => [...prev, `${timestamp} ${statusEmoji} Deployment completed successfully!`]);
          return;
        } else if (status.status === 'failed') {
          statusEmoji = '❌';
          setPipelineLogs(prev => [...prev, `${timestamp} ${statusEmoji} Deployment failed!`]);

          // Fetch logs one more time on failure
          if (gitlabProjectId && (Date.now() - (lastJobsFetched || 0)) > 10000) {
            try {
              const repositoryUrl = getRepositoryUrl(selectedResource?.data);
              const jobs = await unifiedGitDataService.getWorkflowJobs(gitlabProjectId, repositoryUrl, pipelineId);
              for (const job of jobs) {
                if (job.status === 'failed') {
                  const repositoryUrl = getRepositoryUrl(selectedResource?.data);
                  const logs = await unifiedGitDataService.getJobTrace(gitlabProjectId, repositoryUrl, job.id);
                  setPipelineLogs(prev => [
                    ...prev,
                    `\n❌ Failed job ${job.name}:`,
                    logs || '(no logs available)'
                  ]);
                }
              }
            } catch (error) {
              console.error('Error fetching failure logs:', error); /* eslint-disable-line no-console */
            }
          }
          return;
        } else if (status.status === 'canceled') {
          statusEmoji = '🚫';
          setPipelineLogs(prev => [...prev, `${timestamp} ${statusEmoji} Deployment canceled!`]);
          return;
        } else if (status.status === 'running') {
          statusEmoji = '🔄';
        } else if (status.status === 'pending') {
          statusEmoji = '⏳';
        }
        
        setPipelineLogs(prev => {
          const newLogs = [...prev];
          // Update or add status line
          const statusLineIndex = newLogs.findIndex(log => log.includes('Status:'));
          const statusLine = `${timestamp} ${statusEmoji} Status: ${status.status}`;
          if (statusLineIndex >= 0) {
            newLogs[statusLineIndex] = statusLine;
          } else {
            newLogs.push(statusLine);
          }
          return newLogs;
        });
        
        if (attempts < maxAttempts && (status.status === 'pending' || status.status === 'running')) {
          // Continue polling
          setTimeout(poll, 5000);
        } else if (attempts >= maxAttempts) {
          setPipelineLogs(prev => [...prev, `${timestamp} ⚠️ Stopped polling after ${maxAttempts} attempts`]);
        }
      } catch (error) {
        console.error('Error polling pipeline status:', error); /* eslint-disable-line no-console */
        const timestamp = new Date().toLocaleTimeString();
        setPipelineLogs(prev => [...prev, `${timestamp} ❌ Error: ${error.message}`]);
      }
    };
    
    poll();
  };

  // The Code door's right-hand panel is just Preview + Code (see task: "rewrite
  // the right part with just preview and code in a proper way"). "AI" here was
  // a single-purpose Background Agents view (see the FIXME above its render
  // branch) that duplicates the Agents door reachable from the app-switcher
  // dropdown below — dropped entirely rather than kept reachable. "Cloud" has
  // also been removed entirely (not useful for now) — DynamicTaskPanel's
  // "view-logs" action now jumps to Preview and expands the live container
  // logs panel instead. Dashboards are opt-in via a localStorage
  // flag, so that stays reachable, just tucked behind the "More" overflow
  // instead of cluttering the primary two-mode tab bar.
  // Agent used to be a third peer here (task #129) — relocated to the left
  // sidebar's single "Chat" icon selector instead (task #130), alongside
  // Chat and Code, since Agent isn't really a Preview/Code-content-area
  // concern. Restored to the original two-mode tab bar.
  const tabs = [
    {id: 'preview', icon: 'eye', label: 'Preview'},
    {id: 'code', icon: 'code', label: 'Code'},
  ];
  const overflowTabs = [
    (showDashboardTab && {id: 'dashboard', icon: 'grid', label: 'Dashboards'}),
  ].filter(Boolean);
  const isOverflowTabActive = overflowTabs.some(tab => tab.id === activeTab);

  // Hierarchy: Home (project-level: Dashboard/Files) -> a selected Service
  // (Preview/Code). The "Project Configuration" pseudo-resource IS Home —
  // not a preview of anything — so the Preview/Code pill (service-level)
  // is swapped for a Dashboard/Files pill here instead of leaving it
  // showing "Preview" over what's actually the project dashboard.
  const isHomeLevel = selectedResource?.type === 'configuration';
  const showDashboardFilesToggle = isHomeLevel && selectedProject?.id && vfsClient.isVfsBackedProject(selectedProject.id);

  // Search-dropdown items for the service selector (task: "that dropdown
  // must be reused in Service selection" — was a plain native <select>,
  // now the same rich icon+title+desc popover the "Auto" playbook picker
  // already uses, via the shared SearchDropdown component).
  // Where each service is (ready, starting, stopped, failed): the dot in the selector. Shared with the header's counts.
  const { services: runnerServices, known: runnerKnown, canRun: runnerCanRun, refresh: refreshRunner } = useProjectServices(
    selectedProject?.id && vfsClient.isVfsBackedProject(selectedProject.id) ? selectedProject.id : null,
    { intervalMs: 10000 },
  );
  const serviceItems = Object.entries(configuration?.services || {})
    // service.name === '.' is the project root itself — already represented by the pinned
    // "Home" entry below, so it's excluded here to avoid listing the same thing twice.
    // service.name === 'brain' is the project's shared memory/docs folder —
    // vfs-service lists it in `configuration.services` uniformly alongside
    // real app services, but it's not a peer service to switch INTO the
    // same way "app"/"backend" are: it has no independent build/preview,
    // and treating it as one let a stray URL segment or a "brain" file
    // auto-open leave the whole Code view stuck showing "brain" as the
    // active service instead of returning to Home once the agent finished
    // writing docs there — live-reported, repeatedly, as "it creates a
    // brain project instead of using the project brain". It's still fully
    // browsable via Home's own "Files" pill (DashboardView/
    // ProjectFilesBrowser), which already merges brain/ into that view —
    // just never as an independently selectable service here.
    .filter(([_, service]) => service.name !== '.' && service.name !== 'brain' && service.type !== 'custom-resource' && (service.repositoryUrl || service.repoUrl || service.sshUrl || service.vfsBacked))
    .map(([serviceKey, service]) => ({
      value: service.name,
      icon: 'package',
      title: serviceDisplayLabel(service),
      desc: serviceDesc(service),
      key: serviceKey,
      status: scopeDotState(runnerServices, service.name, runnerKnown) || undefined,
    }));
  const selectedServiceValue = selectedResource?.type === 'service' ? selectedResource.data?.name : '';

  // Going Home from the Files view goes back to the Dashboard, the same as pressing the eye: the project name and the
  // Home entry mean "the project overview", not "the project overview, still showing its files".
  const goHome = (value) => {
    if (onServiceSelect) { onServiceSelect(value); }
    if (onDashboardOverviewModeChange) { onDashboardOverviewModeChange('dashboard'); }
  };

  // The scope selector, one for every service-scoped tab (and the phone bar): what the page is about, always visible
  // as a chip: "Service · app ●" for a service, "Scope · Project files" for the Files view outside a service, "Home".
  // The Files view at Home follows the file that is open (`filesScope`): a file under services/app/ is the app's.
  const inFilesView = isHomeLevel && dashboardOverviewMode === 'files';
  const scopeKey = selectedResource?.type === 'service' ? selectedServiceValue : (inFilesView ? filesScope : null);
  // On Preview, each pinned runnable is an entry of its own under its service ("app · docker-compose.yaml").
  const scopeItems = activeTab === 'preview' || selectedResource?.data?.pinnedRunnable
    ? withRunnableItems(serviceItems, pinnedPreviews, (name) => scopeDotState(runnerServices, name, runnerKnown))
    : serviceItems;
  const scopeItem = scopeKey ? scopeItems.find((item) => item.value === scopeKey) || null : null;
  const pickScope = (value) => {
    if (value === '') { goHome(value); return; }
    if (value === '+files-root') { onFilesRoot?.(); return; }
    if (selectedProject?.id && serviceItems.some((item) => item.value === value)) { rememberService(selectedProject.id, value); }
    if (onServiceSelect) { onServiceSelect(value); }
  };
  const scopeProps = ({ caption = true } = {}) => ({
    items: onFilesRoot ? [{ value: '+files-root', icon: 'folder', title: 'Project files', desc: 'Every file of every service' }, ...scopeItems] : scopeItems,
    onSelect: pickScope,
    pinnedItem: { value: '', icon: 'page', title: 'Home', desc: 'Project overview' },
    selectedValue: scopeKey || (inFilesView ? '+files-root' : ''),
    triggerCaption: caption ? (scopeKey || selectedResource?.type === 'service' ? 'Service' : 'Scope') : undefined,
    triggerClassName: 'route-selector-trigger',
    triggerIcon: scopeKey || selectedResource?.type === 'service' ? 'package' : inFilesView ? 'folder' : 'page',
    triggerLabel: scopeItem ? scopeItem.title : selectedResource?.type === 'service' ? serviceDisplayLabel(selectedResource.data) : inFilesView ? 'Project files' : 'Home',
    triggerStatus: scopeItem?.status,
    triggerTitle: 'Switch scope: project Home, the project files, or one of its services',
  });
  useEffect(() => {
    if (selectedProject?.id && selectedServiceValue) { rememberService(selectedProject.id, selectedServiceValue); }
  }, [selectedProject?.id, selectedServiceValue]);

  // The workspace tabs (design "Flow Project Tabs"): Home / Preview / Code are the project's pages; API, Data, Traces and
  // History (Changes, Activity, Sessions) its workspace views (utils/workspaceModel.js); which advanced tabs are on the
  // row, the temporary one and More come from useProjectTabs. Only where `workspace` is given (a vfs-backed project).
  const atHome = isHomeLevel || selectedResource?.type !== 'service';
  const firstServiceValue = preferredService(selectedProject?.id, serviceItems.map((item) => item.value));
  const workspaceTab = workspace ? currentWorkspaceTab({ view: workspace.view, atHome, activeTab, homeMode: dashboardOverviewMode }) : null;
  const projectTabs = useProjectTabs({ projectId: workspace ? selectedProject?.id || null : null, view: workspace?.view || null, page: workspaceTab, signals: workspace?.signals || null });
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  // The phone's Menu sheet never outlives the page it was opened on (2026-10-31: after Quality was picked the sheet
  // stayed on top of the new page): it closes whenever the open tab or the address changes.
  const sheetPage = `${workspaceTab || ''}|${typeof window !== 'undefined' ? window.location.pathname + window.location.search : ''}`;
  useEffect(() => { setMoreSheetOpen(false); }, [sheetPage]);
  const selectWorkspaceTab = (id) => {
    if (!workspace || isTabHidden(projectTabs.hidden, id)) { return; }
    if (VIEW_TABS.includes(id) || id === 'history') {
      workspace.onViewChange(viewFromHistory(id));
      return;
    }
    workspace.onViewChange(null);
    if (id === 'dashboard') { goHome('project-configuration'); return; }
    if (id === 'logs') {
      if (!atHome) { onServiceSelect?.('project-configuration'); }
      onDashboardOverviewModeChange?.('logs');
      return;
    }
    if (id === 'code') {
      if (atHome) { onDashboardOverviewModeChange?.('files'); } else { onTabChange('code'); }
      return;
    }
    if (id === 'preview') {
      if (atHome && firstServiceValue && onServiceSelect) { onServiceSelect(firstServiceValue); }
      onTabChange('preview');
    }
  };
  const hereCount = othersOf(presence).length + listedAgents(presence).length;

  // The actions in the tab row's group menus (design "Flow Project Redesign", Tabs board), each opening what Flow
  // already has, never a page of its own: Ship › Publish and deploys (the Publish dialog); Operate › Backups (Settings ›
  // Sandbox › Backups) and Usage (the sandbox page: plan, credits, compute,
  // storage); Team › Share (the Share dialog: who has access, the invite, the links to the app), Messages and Notes
  // (2026-10-31: one dialog with Comments, Suggestions, Leave a note and Handoff notes as tabs, collab/NotesModal.jsx,
  // `onOpenNotes(tab?)`). Each only where it exists for this project and person. What waits in Notes (the suggestions
  // waiting) is its row's count and, `pending`, the Team button's.
  const canShare = Boolean(selectedProject?.id && projectStorageTier !== 'console');
  const hasNotes = Boolean(showHistoryButton && selectedProject?.id && onOpenNotes);
  const groupActions = workspace ? {
    ship: canShare ? [{ id: 'publish', label: 'Publish and deploys', desc: 'What blocks Publish, publish the app, where it is delivered.', onSelect: () => setShowPublish(true) }] : [],
    operate: [
      ...(config.sandboxFirst ? [
        { id: 'backups', label: 'Backups', desc: 'Your sandbox\'s backups, this project in them: back up now, restore.', onSelect: () => navigateTo('/settings#set-backups') },
      ] : []),
    ],
    team: [
      ...(canShare ? [
        { id: 'share', label: 'Share', desc: 'Who has access, invite people, and links to the app.', onSelect: () => openShare() },
      ] : []),
      // Messages: the chat of the project (Everyone, one conversation per colleague, a viewer too) and the notes for the
      // AI agents (an editor's).
      ...(showHistoryButton && selectedProject?.id ? [{
        id: 'messages', label: 'Messages',
        desc: canMessageAgents(presence?.you?.role) ? 'Chat with everyone here or one person, and leave notes for the AI agents.' : 'Chat with everyone here or one person.',
        onSelect: () => openMessages({ projectId: selectedProject.id, view: 'list' }),
      }] : []),
      ...(hasNotes ? [{ id: 'notes', label: 'Notes', desc: NOTES_DESC, onSelect: () => onOpenNotes(), count: suggestionsPending, pending: suggestionsPending }] : []),
    ],
  } : {};
  // ⌘K's "This project" (search/projectCommands.js): every tab (the tab row's handler), each service, and the actions
  // this header offers this person, built when the palette opens.
  useProjectCommandSource(() => {
    if (!workspace || !selectedProject?.id) { return null; }
    const projectId = selectedProject.id;
    const toast = (message) => window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type: 'error' } }));
    // "Open <service> app": Apps with that service selected, as clicking it there does; where the plan hides Apps,
    // the running app, else Preview on it (a pinned file: the Apps tab).
    const services = projectServiceItems({
      items: serviceItems,
      runner: runnerServices,
      openInApps: isTabHidden(projectTabs.hidden, 'apps') ? null : (serviceName) => workspace.onViewChange({ tab: 'apps', focus: { service: serviceName } }),
      openApp: (serviceName, entry) => openApp(projectId, { serviceName, service: entry }),
      openPreview: (item) => {
        workspace.onViewChange(null);
        rememberService(projectId, item.value);
        onServiceSelect?.(item.value);
        onTabChange('preview');
      },
      openApps: () => selectWorkspaceTab('apps'),
    });
    const currentAdvanced = ADVANCED_TABS.find((tab) => tab.id === workspaceTab && !isTabHidden(projectTabs.hidden, tab.id));
    const pinnedNow = currentAdvanced && projectTabs.prefs?.modes?.[currentAdvanced.id] === 'pinned';
    const groupOf = (id) => [...TAB_GROUPS, TEAM_GROUP].find((group) => group.id === id)?.label || null;
    const actions = [
      ...(config.sandboxFirst && vfsClient.isVfsBackedProject(projectId) ? runCommands({
        projectId, serviceKeys: serviceItems.map((item) => item.key), runnerServices, canRun: runnerCanRun, onChanged: refreshRunner, onError: toast,
      }) : []),
      ...(!isTabHidden(projectTabs.hidden, 'preview') && !(atHome && !firstServiceValue)
        ? [{ id: 'open-preview', label: 'Open preview', group: 'Run', desc: 'The app in the Preview tab', onSelect: () => selectWorkspaceTab('preview') }] : []),
      ...Object.entries(groupActions).flatMap(([group, list]) => list.map((action) => ({ ...action, group: groupOf(group) }))),
      // Notes' sections, each opening Notes on its tab.
      ...(hasNotes ? notesCommands({ open: onOpenNotes, pending: suggestionsPending, group: groupOf('team') }) : []),
      ...(chatOffer ? [{ id: 'chat', label: 'Turn on chat', desc: 'Talk to Flow\'s assistant in your projects', onSelect: () => navigateTo('/features/chat') }] : []),
      ...(currentAdvanced ? [{
        id: 'pin',
        label: `${pinnedNow ? 'Unpin' : 'Pin'} ${currentAdvanced.label}`,
        group: 'Tabs',
        desc: pinnedNow ? 'Back to Auto: on the row only when the project needs it' : 'Keep it on the tab row, in every project',
        onSelect: () => projectTabs.togglePin(currentAdvanced.id),
      }] : []),
      { id: 'tab-settings', label: 'Tab settings', group: 'Tabs', desc: 'Which tabs a project shows', onSelect: () => navigateTo('/settings#set-tabs') },
    ];
    return buildProjectCommands({
      projectName: selectedProject.name || projectId,
      tabs: { hidden: projectTabs.hidden, select: selectWorkspaceTab, previewDisabled: atHome && !firstServiceValue, current: workspaceTab },
      services,
      actions,
    });
  });
  // A comment's file link: the file at its line in Code (Home's Files view), where the plan shows Code.
  const openCommentFile = workspace && !isTabHidden(projectTabs.hidden, 'code')
    ? (code) => { workspace.onViewChange(null); if (!atHome) { onServiceSelect?.('project-configuration'); } navigateTo(`${window.location.pathname}${codeSearch(window.location.search, code)}`); onDashboardOverviewModeChange?.('files'); }
    : null;
  // Open in Code / Suggest a change from the Suggestions dialog (collab/Suggestions.jsx, `flow:open-code`): the file at its
  // line, as a comment's file link opens it; with no file, Code (Home's Files view), to pick one.
  const openCodeRef = useRef(null);
  openCodeRef.current = (detail) => {
    if (detail?.path) {
      if (openCommentFile) { openCommentFile({ path: detail.path, line: detail.line || null }); return; }
      const { service, file } = splitVfsPath(detail.path);
      window.dispatchEvent(new CustomEvent('openFile', { detail: { projectId: selectedProject?.id, serviceName: service || undefined, filePath: file, lineNumber: detail.line || undefined } }));
      return;
    }
    if (workspace) { selectWorkspaceTab('code'); return; }
    if (selectedResource?.type === 'service') { onTabChange('code'); } else { onDashboardOverviewModeChange?.('files'); }
  };
  useEffect(() => {
    const open = (event) => openCodeRef.current?.(event?.detail || {});
    window.addEventListener('flow:open-code', open);
    return () => window.removeEventListener('flow:open-code', open);
  }, []);
  // A tab page's title (WorkspaceView's WorkspaceTitle): the project's name opens Home (`flow:open-project-tab`), its
  // step opens that step's tabs (`flow:open-tab-group`: More on the desktop, handled by WorkspaceTabs; the Menu sheet here).
  const openTabRef = useRef(null);
  openTabRef.current = { select: selectWorkspaceTab, sheet: workspace && compact ? () => setMoreSheetOpen(true) : null };
  useEffect(() => {
    const openTab = (event) => { if (event?.detail?.tab) { openTabRef.current?.select(event.detail.tab); } };
    const openGroup = () => openTabRef.current?.sheet?.();
    window.addEventListener('flow:open-project-tab', openTab);
    window.addEventListener('flow:open-tab-group', openGroup);
    return () => {
      window.removeEventListener('flow:open-project-tab', openTab);
      window.removeEventListener('flow:open-tab-group', openGroup);
    };
  }, []);
  // Messages (the list with its composer), opened from Team ▾, the "In this Project" panel and the avatars' list (collab/Messages.jsx).
  const messagesHost = showHistoryButton && selectedProject?.id ? (
    <MessagesHost onOpenFile={openCommentFile} projectId={selectedProject.id} role={presence?.you?.role || null} />
  ) : null;

  // Controls shared by the legacy bar and the workspace's two rows: the service picker (Preview's also offers to
  // create a service), Refresh, and the split button that opens the service's preview in a new tab.
  const servicePicker = ({ create = false } = {}) => (
    <SearchDropdown
      emptyText='No service matches "{query}"'
      searchPlaceholder="Search services…"
      trailingItem={create ? { value: '+create-service', icon: 'plus', title: '+ Create new service' } : undefined}
      {...scopeProps()}
    />
  );

  const refreshButton = selectedResource?.type === 'service' ? (
    <button
      aria-label="Refresh preview"
      className="unified-action-btn"
      onClick={(e) => onRefresh({ forceClean: e.shiftKey })}
      title="Reload preview (a preview that is not ready is re-rendered; Shift+click: clean install — clears node_modules/lockfile/Vite cache and reinstalls from scratch; use this if a removed dependency still errors after reloading)"
      type="button"
    >
      <Icon name="refresh" size={16} />
      <span>Refresh</span>
    </button>
  ) : null;
  const openInMenu = selectedProject?.id ? (
    <OpenInMenu
      disabled={!previewTabs.some((tab) => tab.serviceName === selectedResource?.data?.name && tab.url)}
      onOpenApp={() => {
        const activeTab = previewTabs.find((tab) => tab.serviceName === selectedResource?.data?.name);
        if (activeTab?.url) {
          window.open(activeTab.url, '_blank', 'noopener,noreferrer');
        }
      }}
      projectId={selectedProject?.id}
      tenantId={tenantId}
    />
  ) : null;
  // Notes, on the bar without the workspace tabs (where Team ▾ of the tab row has it): the Notes dialog (Comments,
  // Suggestions, Leave a note, Handoff notes), its badge the suggestions waiting. Share is its own button.
  const teamMenu = !workspace && hasNotes ? (
    <button
      aria-label={`Notes: comments, suggestions and notes${suggestionsPending > 0 ? ` (${suggestionsPending} suggestions waiting)` : ''}`}
      className="unified-action-btn history-btn collab-menu-btn"
      data-testid="team-menu"
      onClick={() => onOpenNotes()}
      title={NOTES_DESC}
      type="button"
    >
      <Notebook size={16} />
      {!medium && !tightBar && <span>Notes</span>}
      {suggestionsPending > 0 && <span className="collab-badge">{suggestionsPending}</span>}
    </button>
  ) : null;
  // The Code view's "View: Code / Split" (only where a preview is up to split with).
  const viewModeDropdown = (
    <div className="view-dropdown-container" ref={viewDropdownRef}>
      <button
        className="unified-action-btn view-btn"
        onClick={() => setShowViewDropdown(!showViewDropdown)}
        title="Change view mode"
        type="button"
      >
        <Icon name="split" size={16} />
        <span>View: {splitViewMode === 'editor' ? 'Code' : splitViewMode === 'split' ? 'Split' : 'Preview'}</span>
        <Icon name="chevron-down" size={12} />
      </button>
      
      {showViewDropdown && (
        <div className="view-dropdown-menu">
          <div className="view-dropdown-header">View Mode</div>
          <button
            className={`view-dropdown-item ${splitViewMode === 'editor' ? 'active' : ''}`}
            onClick={() => {
              onSplitViewModeChange?.('editor');
              setShowViewDropdown(false);
            }}
            type="button"
          >
            <Icon name="code" size={16} />
            <span>Code Only</span>
          </button>
          <button
            className={`view-dropdown-item ${splitViewMode === 'split' ? 'active' : ''}`}
            onClick={() => {
              onSplitViewModeChange?.('split');
              setShowViewDropdown(false);
            }}
            type="button"
          >
            <Icon name="split" size={16} />
            <span>Split View</span>
          </button>
        </div>
      )}
    </div>
  );
  const fullscreenButton = (
    <button
      aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      className="unified-control-btn"
      onClick={onToggleFullscreen}
      title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      type="button"
    >
      <Icon name={isFullscreen ? 'exit-fullscreen' : 'fullscreen'} size={18} />
    </button>
  );
  const runStatus = config.sandboxFirst && selectedProject?.id && vfsClient.isVfsBackedProject(selectedProject.id) ? (
    <span className="unified-run-status"><ProjectRunStatus projectId={selectedProject.id} serviceKeys={serviceItems.map((item) => item.key)} /></span>
  ) : null;
  // The project's run control (Start all / Stop all, Open app), next to the run state on every tab of the workspace;
  // Home does not repeat it (DashboardView `runToolsInHeader`).
  const runTools = workspace && runStatus ? (
    <ProjectRunTools
      onOpenLogs={() => selectWorkspaceTab('logs')}
      projectId={selectedProject.id}
      serviceKeys={serviceItems.map((item) => item.key)}
      size={compact ? 'small' : 'middle'}
    />
  ) : null;

  if (compact) {
    // Where the project's four places are, from the page the Canvas shows: Home (Dashboard, Files) or a service
    // (Preview, Code — Code is "Files" here: the service's files and the editor).
    // With the workspace tabs: Home, Apps and Files, and Menu for everything else (it lights for any other tab).
    const compactTab = workspace ? currentWorkspaceTab({ view: workspace.view, atHome, activeTab, homeMode: dashboardOverviewMode }) : null;
    const current = workspace
      ? (['dashboard', 'apps', 'files'].includes(compactTab) ? compactTab : 'menu')
      : isHomeLevel || selectedResource?.type !== 'service'
        ? (dashboardOverviewMode === 'files' ? 'files' : 'dashboard')
        : (activeTab === 'code' ? 'files' : activeTab === 'preview' ? 'preview' : 'dashboard');
    const firstService = firstServiceValue;
    const go = (place) => {
      if (workspace && place === 'menu') { setMoreSheetOpen(true); return; }
      if (workspace) { selectWorkspaceTab(place); return; }
      if (place === 'dashboard') { goHome('project-configuration'); return; }
      if (place === 'files') {
        if (selectedResource?.type === 'service') { onTabChange('code'); } else { onDashboardOverviewModeChange?.('files'); }
        return;
      }
      if (place === 'preview') {
        if (selectedResource?.type !== 'service' && firstService && onServiceSelect) { onServiceSelect(firstService); }
        onTabChange('preview');
        return;
      }
      if (place === 'history') { onOpenHistory?.(); }
    };
    // With the workspace tabs, 4 places (design "Flow Project Redesign", Mobile board): Home · Apps · Files · Menu, the
    // rest (Preview, History, the advanced tabs, Team, Turn on chat) in Menu's sheet. A tab the plan hides is not there.
    const places = workspace ? [
      { id: 'dashboard', label: 'Home', Icon: SquaresFour },
      { id: 'apps', label: 'Apps', Icon: AppWindow },
      { id: 'files', label: 'Files', Icon: Files },
      // Menu carries what waits in Team (Suggestions (n)), as Team ▾ does on desktop.
      { id: 'menu', label: 'Menu', Icon: List, popup: true, countWord: 'waiting', count: (groupActions.team || []).reduce((sum, action) => sum + (action.pending || 0), 0) },
    ].filter((place) => !isTabHidden(projectTabs.hidden, place.id)) : [
      { id: 'dashboard', label: 'Dashboard', Icon: SquaresFour },
      { id: 'files', label: 'Files', Icon: FolderSimple },
      { id: 'preview', label: 'Preview', Icon: Eye, disabled: selectedResource?.type !== 'service' && !firstService },
      ...(showHistoryButton && onOpenHistory ? [{ id: 'history', label: 'History', Icon: ClockCounterClockwise }] : []),
    ];
    const previewOfService = previewTabs.find((tab) => tab.serviceName === selectedResource?.data?.name && tab.url);
    const more = [
      // With the workspace tabs, Notes, Share, Publish and Turn on chat are in Menu's sheet (Team, Ship), not here too;
      // the bottom-bar Menu button shows what waits ("n waiting").
      ...(!workspace && hasNotes ? [{ key: 'notes', label: suggestionsPending > 0 ? `Notes (${suggestionsPending})` : 'Notes', icon: <Notebook size={16} /> }] : []),
      ...(!workspace && config.sandboxFirst && isHomeLevel && showDashboardFilesToggle ? [{ key: 'logs', label: 'Logs', icon: <Icon name="terminal" size={16} /> }] : []),
      { type: 'divider' },
      ...(!workspace && selectedProject?.id && projectStorageTier !== 'console' ? [
        { key: 'share', label: 'Share', icon: <Icon name="users" size={16} /> },
        { key: 'publish', label: 'Publish', icon: <Icon name="rocket" size={16} /> },
      ] : []),
      ...(selectedProject?.id ? [{ key: 'open-app', label: 'Open app', icon: <Icon name="link" size={16} />, disabled: !previewOfService }] : []),
      ...(!workspace && chatOffer ? [{ key: 'chat', label: 'Turn on chat', icon: <ChatTeardropText size={16} /> }] : []),
    ].filter((item, index, list) => !(item.type === 'divider' && (index === 0 || index === list.length - 1)));
    const onMore = ({ key }) => {
      if (key === 'notes') { onOpenNotes(); }
      if (key === 'logs') { onDashboardOverviewModeChange?.('logs'); }
      if (key === 'share') { openShare(); }
      if (key === 'publish') { setShowPublish(true); }
      if (key === 'open-app' && previewOfService?.url) { window.open(previewOfService.url, '_blank', 'noopener,noreferrer'); }
      if (key === 'chat') { navigateTo('/features/chat'); }
    };
    return (
      <div className={`unified-top-bar is-compact${phone ? ' is-phone' : ''}${phone && headerAway ? ' is-scrolled-away' : ''}`} data-testid="unified-top-bar-compact" ref={barRef}>
        <div className="compact-top-row">
          <div className="compact-where">
            {/* With the workspace tabs: "Projects / name" and your role, as on desktop; the service picker only on a service. */}
            {workspace ? (
              <WorkspaceCrumb
                onHome={() => selectWorkspaceTab('dashboard')}
                onProjects={() => navigateTo('/projects')}
                projectId={selectedProject?.id}
                projectName={selectedProject?.name}
                role={presence?.you?.role}
              />
            ) : (
              <ProjectBreadcrumb
                atHome={selectedResource?.type !== 'service'}
                onHomeClick={() => goHome('project-configuration')}
                projectName={selectedProject?.name}
              />
            )}
            {!(workspace && atHome) && (
              <SearchDropdown
                emptyText='No service matches "{query}"'
                searchPlaceholder="Search services…"
                {...scopeProps({ caption: false })}
              />
            )}
          </div>
          {presenceProjectId && <PresenceStack compact projectId={presenceProjectId} snapshot={presence} />}
          {more.length > 0 && (
            <Dropdown menu={{ items: more, onClick: onMore }} placement="bottomRight" trigger={['click']}>
              <button aria-label={workspace ? 'More: open app' : 'More: notes, share, publish, open app'} className="compact-more-btn" data-testid="compact-more" type="button">
                <DotsThreeVertical size={20} weight="bold" />
              </button>
            </Dropdown>
          )}
        </div>
        {runTools && <div className="compact-run-row" data-testid="compact-run-row">{runStatus}{runTools}</div>}
        <nav aria-label="Project" className="compact-places">
          {places.map(({ id, label, Icon: PlaceIcon, disabled, count, countWord = 'new', popup }) => (
            <button
              aria-current={current === id ? 'page' : undefined}
              aria-expanded={popup ? moreSheetOpen : undefined}
              aria-haspopup={popup ? 'dialog' : undefined}
              className={`compact-place${current === id ? ' is-on' : ''}`}
              data-testid={`compact-place-${id}`}
              disabled={disabled}
              key={id}
              onClick={() => go(id)}
              title={disabled ? 'This project has no service to preview yet' : label}
              type="button"
            >
              <PlaceIcon aria-hidden size={18} weight={current === id ? 'fill' : 'regular'} />
              <span>{label}</span>
              {count > 0 && <span aria-label={`${count} ${countWord}`} className="compact-place-count">{count > 99 ? '99+' : count}</span>}
            </button>
          ))}
        </nav>
        {workspace && (
          <ProjectMoreSheet
            actions={groupActions}
            changesCount={workspace.changesCount || 0}
            current={compactTab}
            onChat={chatOffer ? () => navigateTo('/features/chat') : null}
            previewDisabled={atHome && !firstServiceValue}
            onClose={() => setMoreSheetOpen(false)}
            onOpen={(id) => { setMoreSheetOpen(false); selectWorkspaceTab(id); }}
            open={moreSheetOpen}
            tabs={projectTabs}
          />
        )}
        {showShare && selectedProject?.id && (
          <ShareDialog
            onAccessChange={onProjectAccessChange}
            onClose={() => setShowShare(false)}
            projectId={selectedProject.id}
            projectName={selectedProject.name || selectedProject.id}
            serviceName={selectedResource?.type === 'service' ? previewServiceName(selectedResource.data?.name) : undefined}
          />
        )}
        {showPublish && selectedProject?.id && (
          <PublishDialog
            onClose={() => setShowPublish(false)}
            onOpenQuality={workspace && !isTabHidden(projectTabs.hidden, 'quality') ? (focus) => workspace.onViewChange({ tab: 'quality', focus: focus || null }) : undefined}
            onOpenApps={workspace && !isTabHidden(projectTabs.hidden, 'apps') ? (service) => workspace.onViewChange({ tab: 'apps', focus: service ? { service } : null }) : undefined}
            projectDescription={selectedProject.description || ''}
            projectId={selectedProject.id}
            projectName={selectedProject.name || selectedProject.id}
            requestedBy={user?.email || ''}
          />
        )}
        {messagesHost}
      </div>
    );
  }

  // The workspace (design "Steady tabs", option A: two fixed rows), the same on every view. Row 1, the project: "Projects
  // / name", your role, the run state … Turn on chat, Team ▾, Publish and the "In this Project" toggle. Row 2, the tabs,
  // always at the same place, and on their right only the current view's tools: Home's Start all and Open app (from the
  // page itself, through `toolsSlotRef`), Preview's service, Refresh and Open, Code's service. Logs is a tab (Operate).
  if (workspace) {
    const sharable = selectedProject?.id && projectStorageTier !== 'console';
    const canSplit = workspaceTab === 'code' && !atHome && previewTabs.length > 0 && previewTabs.some((tab) => tab.url);
    const moreItems = [
      ...(canSplit ? [
        { key: 'view-editor', label: 'View: code only', icon: <Icon name="code" size={16} />, disabled: splitViewMode === 'editor' },
        { key: 'view-split', label: 'View: split', icon: <Icon name="split" size={16} />, disabled: splitViewMode === 'split' },
        { type: 'divider' },
      ] : []),
      // Share is Team's first row (TeamMenu, on the row at every width).
      ...(sharable ? [{ key: 'publish', label: 'Publish', icon: <Icon name="rocket" size={16} /> }] : []),
    ].filter((item, index, list) => !(item.type === 'divider' && index === list.length - 1));
    const onMore = ({ key }) => {
      if (key === 'view-editor') { onSplitViewModeChange?.('editor'); }
      if (key === 'view-split') { onSplitViewModeChange?.('split'); }
      if (key === 'share') { openShare(); }
      if (key === 'publish') { setShowPublish(true); }
    };
    const viewTools = workspaceTab === 'preview' ? (
      <>
        {servicePicker({ create: true })}
        {refreshButton}
        {openInMenu}
      </>
    ) : workspaceTab === 'code' ? (
      <>
        {servicePicker()}
        {canSplit && !medium && viewModeDropdown}
      </>
    ) : null;
    return (
      <>
        <div className="unified-top-bar has-workspace is-workspace-project" data-testid="workspace-project-bar" ref={barRef}>
          <div className="workspace-project-where">
            <WorkspaceCrumb
              onHome={() => selectWorkspaceTab('dashboard')}
              onProjects={() => navigateTo('/projects')}
              projectId={selectedProject?.id}
              projectName={selectedProject?.name}
              role={presence?.you?.role}
            />
            {runStatus}
            {runTools}
          </div>
          <div className="unified-controls">
            {/* No "Turn on chat" here (2026-10-31): the sidebar offers it. */}
            {/* Team ▾ (Share, Messages, Comments, Suggestions, notes), then Publish (2026-10-30: Team left the tab row and
                took Share's place). */}
            <TeamMenu actions={groupActions.team || []} />
            {sharable && !medium && <button className="publish-top-btn" onClick={() => setShowPublish(true)} type="button">Publish</button>}
            {medium && moreItems.length > 0 && (
              <Dropdown menu={{ items: moreItems, onClick: onMore }} placement="bottomRight" trigger={['click']}>
                <button aria-label="More: view, publish" className="unified-control-btn header-more-btn" data-testid="header-more" title="More" type="button">
                  <DotsThreeVertical size={18} weight="bold" />
                </button>
              </Dropdown>
            )}
            {/* Who is here: the docked panel's toggle, or the avatars and their popover where the panel has no room. */}
            {workspace.onTogglePanel ? (
              <WorkspacePanelToggle hereCount={hereCount} onToggle={workspace.onTogglePanel} panelOpen={Boolean(workspace.panelOpen)} />
            ) : presenceProjectId && <PresenceStack max={medium ? 3 : 4} projectId={presenceProjectId} snapshot={presence} />}
            {/* Full screen sits with the panel's toggle: both change the layout, the same on every tab. */}
            {fullscreenButton}
          </div>
          {showShare && sharable && (
            <ShareDialog
              onAccessChange={onProjectAccessChange}
              onClose={() => setShowShare(false)}
              projectId={selectedProject.id}
              projectName={selectedProject.name || selectedProject.id}
              serviceName={selectedResource?.type === 'service' ? previewServiceName(selectedResource.data?.name) : undefined}
            />
          )}
          {showPublish && sharable && (
            <PublishDialog
              onClose={() => setShowPublish(false)}
              onOpenQuality={workspace && !isTabHidden(projectTabs.hidden, 'quality') ? (focus) => workspace.onViewChange({ tab: 'quality', focus: focus || null }) : undefined}
              onOpenApps={workspace && !isTabHidden(projectTabs.hidden, 'apps') ? (service) => workspace.onViewChange({ tab: 'apps', focus: service ? { service } : null }) : undefined}
              projectDescription={selectedProject.description || ''}
              projectId={selectedProject.id}
              projectName={selectedProject.name || selectedProject.id}
              requestedBy={user?.email || ''}
            />
          )}
          {messagesHost}
        </div>
        <WorkspaceTabs
          changesCount={workspace.changesCount || 0}
          current={workspaceTab}
          onOpenSettings={() => navigateTo('/settings#set-tabs')}
          onSelect={selectWorkspaceTab}
          previewDisabled={atHome && !firstServiceValue}
          previewWatchers={previewWatchers(presence)}
          groupActions={groupActions}
          tabs={projectTabs}
          tools={viewTools}
          toolsSlotRef={workspaceTab === 'dashboard' ? workspace.toolsSlotRef || null : null}
        />
      </>
    );
  }

  return (
    <div className="unified-top-bar" ref={barRef}>
      {/* Left Side: Home breadcrumb + service selector — the "where am I"
          part of the hierarchy, read first, left to right. */}
      <div className="unified-center-content">
        {/* Code Tab: Service Dropdown + Git Controls */}
        {activeTab === 'code' && (
          <>
            <ProjectBreadcrumb
              projectName={selectedProject?.name}
              onHomeClick={() => goHome('project-configuration')}
              atHome={selectedResource?.type !== 'service'}
            />
            {/* Same rich search+icon+desc popover as the "Auto" playbook
                picker (task: "that dropdown must be reused in Service
                selection") — `configuration` can still be null/loading
                here, e.g. right after a project is created; `serviceItems`
                is just an empty list until it resolves, same as the old
                `<select>` showed an empty option list. */}
            {servicePicker()}
          </>
        )}

        {/* Preview Tab: Service Dropdown + Refresh */}
        {activeTab === 'preview' && (
          <>
            <ProjectBreadcrumb
              projectName={selectedProject?.name}
              onHomeClick={() => goHome('project-configuration')}
              atHome={selectedResource?.type !== 'service'}
            />
            {/* Same rich search+icon+desc popover as the "Auto" playbook
                picker (task: "that dropdown must be reused in Service
                selection") — was a plain native <select>. Fully controlled
                by `selectedServiceValue` (derived from `selectedResource`
                React state), so — unlike the old native <select>'s own DOM
                value — nothing needs a setTimeout hack to "reset" after
                picking "+ Create new service": once that action starts a
                new project, `selectedResource` just isn't a service yet,
                and the trigger naturally shows Home/whatever it already
                was. `configuration` can still be null/loading here (e.g.
                right after a project is created) — `serviceItems` is just
                an empty list until it resolves, same as before. */}
            {servicePicker({ create: true })}
            {refreshButton}
          </>
        )}

        {/* On every page of the project (Home, a service's Preview or Code). */}
        {/* History (vfs-backed projects only): now the workspace tabs (workspace/WorkspaceTabs); this button is for projects without them. */}
        {showHistoryButton && (
          <button
            aria-label="History: timeline, changes by person, activity"
            className="unified-action-btn history-btn"
            data-testid="open-history"
            onClick={() => onOpenHistory?.()}
            title="History: checkpoints and rewind, changes by person, activity"
            type="button"
          >
            <ClockCounterClockwise aria-hidden size={16} />
            {!medium && <span>History</span>}
          </button>
        )}
        {teamMenu}
        {/* Dashboard Tab: Project name */}
        {activeTab === 'dashboard' && selectedProject && (
          <div className="project-name-display">
            <Icon name="folder" size={16} />
            <span>{selectedProject.name}</span>
            <span className={`tier-badge tier-badge-${projectStorageTier}`} title="Project storage tier">
              {projectStorageTier === 'vfs' && 'VFS'}
              {projectStorageTier === 'catalog' && 'Catalog'}
              {projectStorageTier === 'console' && 'Console'}
            </span>
          </div>
        )}
        {/* Sandbox-first: how many of this project's services run, next to where you are (the workspace's Home has its
            own bar: its strip says it). */}
        {runStatus}
      </div>

      {/* View-mode pill — the "what am I looking at, at this level" part of
          the hierarchy, read after Home/Service. At Home level this is
          Dashboard/Files (task #146's control, relocated here so its label
          never again reads "Preview" over what's actually the project
          dashboard); once a real service is selected it's the original
          Preview/Code pill. */}
      {showDashboardFilesToggle ? (
        <div className="unified-tabs">
          <Segmented
            className="header-segmented"
            title={config.sandboxFirst ? 'Switch between Dashboard, Files and Logs' : 'Switch between Dashboard and Files'}
            value={dashboardOverviewMode}
            onChange={onDashboardOverviewModeChange}
            options={[
              {
                value: 'dashboard',
                label: (
                  <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}} title="Dashboard">
                    <Icon name="eye" size={14} />
                    {dashboardOverviewMode === 'dashboard' && <span>Dashboard</span>}
                  </span>
                ),
              },
              {
                value: 'files',
                label: (
                  <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}} title="Files">
                    <Icon name="folder" size={14} />
                    {dashboardOverviewMode === 'files' && <span>Files</span>}
                  </span>
                ),
              },
              // Sandbox-first: each service's build and running output, without chat or MCP.
              ...(config.sandboxFirst ? [{
                value: 'logs',
                label: (
                  <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}} title="Logs">
                    <Icon name="terminal" size={14} />
                    {dashboardOverviewMode === 'logs' && <span>Logs</span>}
                  </span>
                ),
              }] : []),
            ]}
          />
        </div>
      ) : (
        <div className="unified-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`unified-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => onTabChange(tab.id)}
              title={tab.label}
              aria-label={tab.label}
              type="button"
            >
              <span className="tab-icon">
                <Icon name={tab.icon} size={18} />
              </span>
              {activeTab === tab.id && <span className="tab-label">{tab.label}</span>}
            </button>
          ))}
          {overflowTabs.length > 0 && (
            <Dropdown
              menu={{
                items: overflowTabs.map(tab => ({key: tab.id, label: tab.label, icon: <Icon name={tab.icon} size={14} />})),
                onClick: ({key}) => onTabChange(key),
                selectedKeys: isOverflowTabActive ? [activeTab] : [],
              }}
              placement="bottomLeft"
              trigger={['click']}
            >
              <button
                className={`unified-tab ${isOverflowTabActive ? 'active' : ''}`}
                title={isOverflowTabActive ? overflowTabs.find(tab => tab.id === activeTab)?.label : 'More'}
                aria-label={isOverflowTabActive ? overflowTabs.find(tab => tab.id === activeTab)?.label : 'More'}
                type="button"
              >
                <span className="tab-icon">
                  <Icon name={isOverflowTabActive ? overflowTabs.find(tab => tab.id === activeTab)?.icon : 'grid'} size={18} />
                </span>
                {isOverflowTabActive && <span className="tab-label">{overflowTabs.find(tab => tab.id === activeTab)?.label}</span>}
              </button>
            </Dropdown>
          )}
        </div>
      )}

      {/* Right Side: View dropdown, Deploy dropdown + Fullscreen toggle */}
      <div className="unified-controls">

        {/* View Mode Dropdown - Show on Code tab when preview is available */}
        {activeTab === 'code' && !medium && previewTabs.length > 0 && previewTabs.some(tab => tab.url) && viewModeDropdown}

        {/* Deploy Dropdown - Preview tab only; the Code (Monaco) tab no longer
            surfaces git/deploy controls at all per explicit product decision —
            "Send to Production" on the Project Home Dashboard is the real
            promote-to-production path now. */}
        {activeTab === 'preview' && configuration?.environments && configuration.environments.length > 0 && (
          <div className="deploy-dropdown-container" ref={deployDropdownRef}>
            <button
              className={`unified-action-btn deploy-btn ${isDeploying ? 'deploying' : ''}`}
              onClick={() => setShowDeployDropdown(!showDeployDropdown)}
              disabled={isDeploying}
              title="Deploy to environment"
              type="button"
            >
              <Icon name="rocket" size={16} />
              <span>{isDeploying ? 'Deploying...' : 'Deploy'}</span>
              <Icon name="chevron-down" size={12} />
            </button>
            
            {showDeployDropdown && (
              <div className="deploy-dropdown-menu">
                <div className="deploy-dropdown-header">Deploy to Environment</div>
                {configuration.environments.map((env, idx) => (
                  <div key={idx} className="deploy-dropdown-item">
                    <div className="deploy-env-info">
                      <div className="deploy-env-name">{env.label || env.envId}</div>
                      {env.hosts && env.hosts.length > 0 && (
                        <div className="deploy-env-urls">
                          {env.hosts.map((hostObj, hIdx) => (
                            <a
                              key={hIdx}
                              href={`${hostObj.scheme || 'https'}://${hostObj.host}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="deploy-env-url"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {hostObj.host}
                              <Icon name="link" size={12} />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      className="deploy-env-btn"
                      onClick={() => handleDeploy(env)}
                      disabled={isDeploying}
                      type="button"
                    >
                      Deploy
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Real-time Collaboration Panel */}
        {localStorage.getItem('enableCollaboration') === 'true' && selectedProject?.id && user && (
          <CollaborationPanel
            projectId={selectedProject.id}
            userId={user.id || user.sub || user.email}
            username={user.name || user.email || 'Anonymous'}
          />
        )}
        
        {/* Live-requested: "open in should code also in code editor bar" —
            the Open in split button, here for the Code tab too. The primary
            action reuses whichever preview tab matches the currently-selected
            service. */}
        {/* Sandbox-first (docs/SANDBOX_FIRST.md): the way to turn chat on (the run status sits on the left). */}
        {config.sandboxFirst && !chatFeatureOn && chatAvailable && (
          <button className="share-top-btn" onClick={() => navigateTo('/features/chat')} style={{ whiteSpace: 'nowrap' }} title="Talk to Flow's assistant in your projects" type="button">
            Turn on chat
          </button>
        )}
        {/* Who else is here, live (people round, AI agents square with their driver's initials). */}
        {presenceProjectId && <PresenceStack max={medium ? 3 : 4} projectId={presenceProjectId} snapshot={presence} />}
        {/* Share: people and public preview links (docs/project-sharing.md). Next to Publish, same tiers. */}
        {selectedProject?.id && projectStorageTier !== 'console' && !medium && (
          <button
            aria-haspopup="dialog"
            className="share-top-btn"
            onClick={() => openShare()}
            title="Share this project and its preview"
            type="button"
          >
            Share
          </button>
        )}
        {showShare && selectedProject?.id && (
          <ShareDialog
            onAccessChange={onProjectAccessChange}
            onClose={() => setShowShare(false)}
            projectId={selectedProject.id}
            projectName={selectedProject.name || selectedProject.id}
            serviceName={selectedResource?.type === 'service' ? previewServiceName(selectedResource.data?.name) : undefined}
          />
        )}
        {selectedProject?.id && projectStorageTier !== 'console' && !medium && (
          <button className="publish-top-btn" onClick={() => setShowPublish(true)} type="button">
            Publish
          </button>
        )}
        {medium && (() => {
          const canSplit = activeTab === 'code' && previewTabs.length > 0 && previewTabs.some((tab) => tab.url);
          const items = [
            ...(canSplit ? [
              { key: 'view-editor', label: 'View: code only', icon: <Icon name="code" size={16} />, disabled: splitViewMode === 'editor' },
              { key: 'view-split', label: 'View: split', icon: <Icon name="split" size={16} />, disabled: splitViewMode === 'split' },
              { type: 'divider' },
            ] : []),
            ...(selectedProject?.id && projectStorageTier !== 'console' ? [
              { key: 'share', label: 'Share', icon: <Icon name="users" size={16} /> },
              { key: 'publish', label: 'Publish', icon: <Icon name="rocket" size={16} /> },
            ] : []),
          ];
          if (items.length === 0) { return null; }
          const onClick = ({ key }) => {
            if (key === 'view-editor') { onSplitViewModeChange?.('editor'); }
            if (key === 'view-split') { onSplitViewModeChange?.('split'); }
            if (key === 'share') { openShare(); }
            if (key === 'publish') { setShowPublish(true); }
          };
          return (
            <Dropdown menu={{ items, onClick }} placement="bottomRight" trigger={['click']}>
              <button aria-label="More: view, share, publish" className="unified-control-btn header-more-btn" data-testid="header-more" title="More" type="button">
                <DotsThreeVertical size={18} weight="bold" />
              </button>
            </Dropdown>
          );
        })()}
        {showPublish && selectedProject?.id && (
          <PublishDialog
            onClose={() => setShowPublish(false)}
            onOpenQuality={workspace && !isTabHidden(projectTabs.hidden, 'quality') ? (focus) => workspace.onViewChange({ tab: 'quality', focus: focus || null }) : undefined}
            onOpenApps={workspace && !isTabHidden(projectTabs.hidden, 'apps') ? (service) => workspace.onViewChange({ tab: 'apps', focus: service ? { service } : null }) : undefined}
            projectDescription={selectedProject.description || ''}
            projectId={selectedProject.id}
            projectName={selectedProject.name || selectedProject.id}
            requestedBy={user?.email || ''}
          />
        )}

        {openInMenu}

        {fullscreenButton}
      </div>

      {/* Pipeline Logs Modal */}
      {showPipelineModal && currentPipeline && createPortal(
        <div className="modal-overlay" onClick={() => setShowPipelineModal(false)}>
          <div className="pipeline-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pipeline-modal-header">
              <h3>🚀 Deployment Pipeline</h3>
              <button 
                className="modal-close-btn"
                onClick={() => setShowPipelineModal(false)}
                type="button"
              >
                ×
              </button>
            </div>
            <div className="pipeline-modal-info">
              <div><strong>Environment:</strong> {currentPipeline.environmentLabel}</div>
              <div><strong>Pipeline ID:</strong> {currentPipeline.id}</div>
              {currentPipeline.url && (
                <div>
                  <strong>GitLab:</strong>{' '}
                  <a 
                    href={currentPipeline.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="pipeline-gitlab-link"
                  >
                    View in Tool <Icon name="link" size={12} />
                  </a>
                </div>
              )}
            </div>
            <div className="pipeline-logs-container">
              {pipelineLogs.map((log, idx) => (
                <div key={idx} className="pipeline-log-entry">{log}</div>
              ))}
            </div>
            <div className="pipeline-modal-actions">
              <button
                onClick={() => setShowPipelineModal(false)}
                type="button"
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

UnifiedTopBar.propTypes = {
  activeTab: PropTypes.string.isRequired,
  tenantId: PropTypes.string,
  configuration: PropTypes.object,
  dashboardOverviewMode: PropTypes.oneOf(['dashboard', 'files', 'logs']),
  onDashboardOverviewModeChange: PropTypes.func,
  isFullscreen: PropTypes.bool,
  onNewConversation: PropTypes.func,
  // (tab?) => void: Team ▾ › Notes (collab/NotesModal.jsx), on a tab ('comments', 'suggestions', 'leave', 'handoff').
  onOpenNotes: PropTypes.func,
  suggestionsPending: PropTypes.number,
  onOpenHistory: PropTypes.func,
  onRefresh: PropTypes.func,
  onServiceSelect: PropTypes.func,
  filesScope: PropTypes.string,
  onFilesRoot: PropTypes.func,
  onShowHistory: PropTypes.func,
  onProjectAccessChange: PropTypes.func,
  onSplitViewModeChange: PropTypes.func,
  onTabChange: PropTypes.func.isRequired,
  onToggleFullscreen: PropTypes.func.isRequired,
  previewTabs: PropTypes.array,
  /** The pinned runnables the Preview lists under their services (utils/runnablePreview pinnedPreviewEntries). */
  pinnedPreviews: PropTypes.array,
  selectedProject: PropTypes.object,
  selectedResource: PropTypes.object,
  showHistoryButton: PropTypes.bool,
  showDashboardTab: PropTypes.bool,
  splitViewMode: PropTypes.oneOf(['editor', 'split', 'preview']),
  workspace: PropTypes.shape({
    view: PropTypes.object,
    onViewChange: PropTypes.func.isRequired,
    changesCount: PropTypes.number,
    panelOpen: PropTypes.bool,
    onTogglePanel: PropTypes.func,
    onOpenPanel: PropTypes.func,
    toolsSlotRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  }),
};

export default UnifiedTopBar;
